"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Award, ShieldCheck, MapPin, Users, FileCheck2, HardHat } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const trustPoints = [{
  icon: ShieldCheck,
  title: "Confirmed accreditations",
  description: "Audited and verified member of NASC and CHAS, upholding strict health, safety, and operational standards on every site.",
  tag: "Safety verified"
}, {
  icon: HardHat,
  title: "20+ years hands-on experience",
  description: "Led by Pete Wood with over two decades of direct scaffolding leadership, technical problem-solving, and complex access management.",
  tag: "Proven leadership"
}, {
  icon: Users,
  title: "Family-run accountability",
  description: "Commercial-grade execution paired with direct owner involvement, careful communication, and genuine personal care for every client.",
  tag: "Direct responsibility"
}, {
  icon: MapPin,
  title: "Dedicated regional coverage",
  description: "Providing responsive, planned access across Berkshire, Surrey, Hampshire, Buckinghamshire, London, and the wider Home Counties.",
  tag: "Regional reach"
}];
const accreditations = [{
  name: "NASC",
  label: "National Access & Scaffolding Confederation"
}, {
  name: "CHAS",
  label: "Contractors Health and Safety Assessment Scheme"
}];
export default function Credibility() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="credibilitytrust" data-nav="light" className="bg-background py-16 sm:py-20 md:py-24 border-t border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl">
          <Badge variant="outline" className="mb-3.5 border-border text-foreground font-medium">
            Verified standards
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Direct accountability, verified standards
          </h2>
          <p className="mt-3 text-lg text-muted-foreground">
            Every scaffold is built on confirmed industry accreditations, twenty years of hands-on leadership, and active oversight across the South East.
          </p>
        </div>

        {/* Accreditation Badges Strip */}
        <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {accreditations.map((item, index) => <div key={item.name} data-index={index} className="flex items-center gap-3 rounded-lg border border-border bg-card p-3.5 shadow-2xs">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent text-foreground">
                <FileCheck2 className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-card-foreground">
                  {item.name}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {item.label}
                </div>
              </div>
            </div>)}
        </div>

        {/* Core Trust Points Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {trustPoints.map((point, index) => {
          const Icon = point.icon;
          return <motion.div key={point.title} variants={itemVariants} data-index={index}>
                <Card className="h-full border border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="text-2xs uppercase tracking-wider font-semibold text-muted-foreground bg-muted px-2 py-0.5 rounded-sm">
                        {point.tag}
                      </span>
                    </div>
                    <CardTitle className="font-default text-lg font-semibold text-card-foreground">
                      {point.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {point.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Verification & Reviews Callout */}
        <div className="mt-10 rounded-xl border border-border bg-muted/40 p-6 sm:p-8">
          <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Award className="h-4 w-4 text-primary" />
                <span>Genuine customer feedback</span>
              </div>
              <p className="text-sm text-muted-foreground max-w-2xl">
                The reviews below are genuine feedback from BMF Scaffolding customers.
              </p>
            </div>

            <Button asChild variant="outline" className="shrink-0">
              <Link to="#CustomerReviews" className="flex items-center gap-2">
                <span>Read our Google Reviews</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}